import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Badge,
  IconButton,
  Chip,
  Paper,
  MenuItem,
  Select,
  InputAdornment,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
   Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getProductsByConnection } from "../redux/slices/sellerProductSlice";
import { addToCart, getCart } from "../redux/slices/cartSlice";
import PageTitle from "../Components/PageTitle";

export default function ViewProducts() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { connectionId, otherUserId } = location.state || {};
  
  const userId = useSelector((state) => state.user?.user?._id);
  const cart = useSelector((state) => state.cart?.cart);

  const {
    productsByConnection: allProducts = [],
    connectionPage,
    connectionTotalPages,
    connectionTotalProducts,
    loading,
    error
  } = useSelector((state) => state.products);

  const [quantities, setQuantities] = useState({});
  const [localCartCount, setLocalCartCount] = useState(0);
  const [filter, setFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // All categories state
  const [allCategories, setAllCategories] = useState([]);

  // Fetch ALL products once to extract categories
  useEffect(() => {
    if (connectionId && otherUserId) {
      dispatch(getProductsByConnection({ 
        connectionId, 
        otherUserId,
        page: 1,
        limit: 10000  // Fetch all to get categories
      }))
        .unwrap()
        .then((result) => {
          const products = result?.products || result?.data || result || [];
          
          // Extract unique categories
          const categories = Array.from(
            new Set(
              products
                .map((p) => p.category?.name)
                .filter(Boolean)
            )
          ).sort(); 
          
          setAllCategories(categories);
        })
        .catch((error) => {
          console.error("Failed to fetch categories:", error);
        });
    }
  }, [dispatch, connectionId, otherUserId]);

  // FILTERING AND SEARCHING
  const filteredProducts = useMemo(() => {
    if (!allProducts || allProducts.length === 0) return [];
    
    let result = [...allProducts];
    
    // Apply category filter
    if (filter !== "All") {
      result = result.filter(p => p.category?.name === filter);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((p) => {
        return (
          p.name?.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.category?.name?.toLowerCase().includes(query)
        );
      });
    }
    
    return result;
  }, [allProducts, filter, searchQuery]);

  // filtered results
  const paginatedProducts = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, page, limit]);

  // Products to display
  const displayProducts = paginatedProducts;

  // Calculate total pages based on filtered results
  const totalPages = useMemo(() => {
    return Math.ceil(filteredProducts.length / limit) || 1;
  }, [filteredProducts.length, limit]);

  // Total products count
  const totalProductsCount = filteredProducts.length;

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setPage(1);
  }, [filter, searchQuery]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle limit change
  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setLimit(newLimit);
    setPage(1);
  };

  // Handle search
  const handleSearch = () => {
    setPage(1);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Handle Enter key for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Quantity Change
  const handleQtyChange = (id, value) => {
    if (value < 0) return;
    setQuantities({ ...quantities, [id]: value });
  };

  // ADD All to Cart
  const handleAddAll = async () => {
    const items = Object.entries(quantities)
      .filter(([id, qty]) => {
        const productExists = displayProducts?.some(p => p._id === id);
        return qty > 0 && productExists;
      })
      .map(([id, qty]) => ({
        productId: id,
        quantity: qty,
      }));

    if (items?.length === 0)
      return toast.info("Please enter quantity for at least one product.");

    try {
      await dispatch(addToCart({ userId, items })).unwrap();
      toast.success("Products added to cart!");
      const addedProductCount = items.length;
      setLocalCartCount((prev) => prev + addedProductCount);
      const sellerIds = [
        ...new Set(allProducts?.map((p) => p.user?._id).filter(Boolean)),
      ];
      await Promise.all(
        sellerIds?.map((sellerId) =>
          dispatch(getCart({ userId, seller: sellerId })).unwrap()
        )
      );

      setQuantities({});
    } catch (error) {
      toast.error("Failed to add items to cart");
    }
  };

  // Extract seller & buyer categories
  const sellerIds = useMemo(
    () => [...new Set(allProducts?.map((p) => p.user?._id).filter(Boolean))],
    [allProducts]
  );

  const buyerCategories = useMemo(
    () => [
      ...new Set(
        allProducts
          ?.flatMap((p) => p.productVisibility?.map((v) => v.buyerCategory))
          .filter(Boolean)
      ),
    ],
    [allProducts]
  );

  useEffect(() => {
    if (userId && sellerIds.length > 0) {
      dispatch(getCart({ userId, seller: sellerIds, buyerCategories }));
    }
  }, [dispatch, userId, sellerIds, buyerCategories]);

  // PRODUCT CATEGORIES FOR FILTER DROPDOWN
  const productCategories = useMemo(() => {
    return [
      { key: "All", label: "All Products" },
      ...allCategories.map((cat) => ({ key: cat, label: cat })),
    ];
  }, [allCategories]);

  // Total Cart Count
  const totalCartCount = useMemo(() => {
    const backendCount = Array.isArray(cart) ? cart.length : 0;
    return backendCount + localCartCount;
  }, [cart, localCartCount]);

  return (
    <>
    <PageTitle title="View Products - BizBridge"
    description="View and manage products from your connected sellers"
    keywords="view products, connected sellers, buyer dashboard"
    url="/buyer-dashboard/view-products"
    />
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: 3,
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
      }}
    >
       {/* Breadcrumb */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          width: "fit-content",
          backgroundColor: "#fff",
          // ml: { xs: 1, sm: 2.9 },
          padding: "13px",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/buyer-dashboard/seller-network"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Seller Network
        </MUILink>
        <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
          View Products
        </Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 2 }} />

      <Typography fontWeight={700} fontSize={22} sx={{ mb: 3 }}>
        View Products
      </Typography>

      {/* Filters Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 3,
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        {/* Search Field */}
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            placeholder="Search products by name, description, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ p: 0.5, mr: 1 }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Clear
                      </Typography>
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "white",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            size="small"
          />
        </Box>

        {/* Category Dropdown */}
        <Box sx={{ minWidth: 200 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={filter}
              label="Category"
              onChange={(e) => setFilter(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              IconComponent={ExpandMoreIcon}
            >
              {productCategories.map((category) => (
                <MenuItem key={category.key} value={category.key}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Items per page selector */}
        <Box sx={{ minWidth: 120 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="limit-select-label">Items</InputLabel>
            <Select
              labelId="limit-select-label"
              value={limit.toString()}
              label="Items"
              onChange={handleLimitChange}
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
              }}
            >
              <MenuItem value="10">10 per page</MenuItem>
              <MenuItem value="20">20 per page</MenuItem>
              <MenuItem value="50">50 per page</MenuItem>
              <MenuItem value="100">100 per page</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Active Filters Display */}
      {(filter !== "All" || searchQuery.trim()) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Active filters:
          </Typography>
          {filter !== "All" && (
            <Chip
              label={`Category: ${filter}`}
              size="small"
              onDelete={() => setFilter("All")}
              sx={{ fontSize: "0.75rem" }}
            />
          )}
          {searchQuery.trim() && (
            <Chip
              label={`Search: "${searchQuery}" (${filteredProducts.length} found)`}
              size="small"
              onDelete={handleClearSearch}
              sx={{ fontSize: "0.75rem" }}
            />
          )}
        </Box>
      )}

      {/* Loading State */}
      {loading && displayProducts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading products...</Typography>
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
          <Typography>Error: {error}</Typography>
        </Box>
      )}

      {/* Products Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, fontSize: "16px" }}>
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: "16px" }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: "16px" }}>
                  Category
                </TableCell>
                 <TableCell sx={{ fontWeight: 800, fontSize: "16px" }}>
                  Unit
                </TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: "16px" }}>
                  Price
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 800, fontSize: "16px", textAlign: "center" }}
                >
                  Quantity
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {displayProducts.length > 0 ? (
                displayProducts.map((product, index) => {
                  const serialNumber = (page - 1) * limit + index + 1;
                  
                  return (
                    <TableRow key={product._id}>
                      <TableCell sx={{ fontSize: "15px" }}>
                        {serialNumber}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            color: "#22364a",
                            fontWeight: 600,
                            fontSize: "15px",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                              color: "#0b4064",
                            },
                          }}
                          onClick={() =>
                            navigate(
                              `/buyer-dashboard/product-details/${product._id}`,
                              { state: { product } }
                            )
                          }
                        >
                          {product.name}
                        </Box>
                      </TableCell> 

                      <TableCell sx={{ fontSize: "14px", color: "#555" }}>
                        {product.category?.name || "Uncategorized"}
                      </TableCell>

                      <TableCell sx={{ fontSize: "14px", color: "#555" }}>
                        {product.unit || ""}
                      </TableCell>


                      <TableCell
                        sx={{ fontSize: "15px", color: "#555",  }}
                      >
                        ₹{product.price}
                      </TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        <TextField
                          type="number"
                          size="small"
                          value={quantities[product._id] || 0}
                          onChange={(e) =>
                            handleQtyChange(
                              product._id,
                              parseInt(e.target.value) || 0
                            )
                          }
                          sx={{
                            width: "70px",
                            "& .MuiInputBase-root": {
                              borderRadius: "8px",
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                      {searchQuery.trim() || filter !== "All"
                        ? `No products found${filter !== "All" ? ` in category "${filter}"` : ''}${searchQuery.trim() ? ` matching "${searchQuery}"` : ''}`
                        : "No products available"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination - Always visible when there are filtered results */}
      {totalPages > 1 && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                },
                '& .Mui-selected': {
                  backgroundColor: '#0b4064 !important',
                  color: 'white',
                }
              }}
            />
          </Stack>
        </Box>
      )}

      {/* Bottom Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            onClick={handleAddAll}
            disabled={displayProducts.length === 0}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              backgroundColor: "#0b4064",
              color: "#fff",
              "&:hover": { backgroundColor: "#0b4064" },
              "&.Mui-disabled": {
                backgroundColor: "#f0f0f0",
                color: "#aaa",
              },
            }}
          >
            Add to Cart
          </Button>

          <Badge
            badgeContent={totalCartCount}
            color="primary"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <IconButton
              onClick={() => {
                navigate("/buyer-dashboard/cart", {
                  state: { buyerCategories, sellerIds, userId },
                });
              }}
              sx={{
                backgroundColor: "#e0f2f1",
                borderRadius: "50%",
                width: 50,
                height: 50,
                color: "#22364a",
                "&:hover": { backgroundColor: "#b2dfdb" },
              }}
            >
              <ShoppingCartIcon fontSize="medium" />
            </IconButton>
          </Badge>
        </Box>

        {/* Pagination Summary */}
        {displayProducts.length > 0 && (
          <Typography variant="body2" color="primary">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, totalProductsCount)} of{" "}
            {totalProductsCount} products
          </Typography>
        )}
      </Box>
    </Box>
    </>
  );
}


// import React, { useEffect, useState, useMemo } from "react";
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   Badge,
//   IconButton,
//   Chip,
//   Paper,
//   MenuItem,
//   Select,
//   InputAdornment,
//   FormControl,
//   InputLabel,
//   Pagination,
//   Stack,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Avatar,
//   Divider,
//   Input,
// } from "@mui/material";
// import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
// import SearchIcon from "@mui/icons-material/Search";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import AddIcon from "@mui/icons-material/Add";
// import RemoveIcon from "@mui/icons-material/Remove";
// import { useDispatch, useSelector } from "react-redux";
// import { useLocation, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import { getProductsByConnection } from "../redux/slices/sellerProductSlice";
// import { addToCart, getCart } from "../redux/slices/cartSlice";

// export default function ViewProducts() {
//   const dispatch = useDispatch();
//   const location = useLocation();
//   const navigate = useNavigate();
//   const { connectionId, otherUserId } = location.state || {};
  
//   const userId = useSelector((state) => state.user?.user?._id);
//   const cart = useSelector((state) => state.cart?.cart);

//   const {
//     productsByConnection: allProducts = [],
//     connectionPage,
//     connectionTotalPages,
//     connectionTotalProducts,
//     loading,
//     error
//   } = useSelector((state) => state.products);

//   const [quantities, setQuantities] = useState({});
//   const [localCartCount, setLocalCartCount] = useState(0);
//   const [filter, setFilter] = useState("All");
//   const [searchQuery, setSearchQuery] = useState("");
  
//   // Pagination states
//   const [page, setPage] = useState(1);
//   const [limit, setLimit] = useState(12); // Changed to 12 for grid layout

//   // All categories state
//   const [allCategories, setAllCategories] = useState([]);

//   // Fetch ALL products once to extract categories
//   useEffect(() => {
//     if (connectionId && otherUserId) {
//       dispatch(getProductsByConnection({ 
//         connectionId, 
//         otherUserId,
//         page: 1,
//         limit: 10000
//       }))
//         .unwrap()
//         .then((result) => {
//           const products = result?.products || result?.data || result || [];
          
//           // Extract unique categories
//           const categories = Array.from(
//             new Set(
//               products
//                 .map((p) => p.category?.name)
//                 .filter(Boolean)
//             )
//           ).sort(); 
          
//           setAllCategories(categories);
//         })
//         .catch((error) => {
//           console.error("Failed to fetch categories:", error);
//         });
//     }
//   }, [dispatch, connectionId, otherUserId]);

//   // FILTERING AND SEARCHING
//   const filteredProducts = useMemo(() => {
//     if (!allProducts || allProducts.length === 0) return [];
    
//     let result = [...allProducts];
    
//     // Apply category filter
//     if (filter !== "All") {
//       result = result.filter(p => p.category?.name === filter);
//     }
    
//     // Apply search filter
//     if (searchQuery.trim()) {
//       const query = searchQuery.toLowerCase().trim();
//       result = result.filter((p) => {
//         return (
//           p.name?.toLowerCase().includes(query) ||
//           p.description?.toLowerCase().includes(query) ||
//           p.category?.name?.toLowerCase().includes(query)
//         );
//       });
//     }
    
//     return result;
//   }, [allProducts, filter, searchQuery]);

//   // filtered results
//   const paginatedProducts = useMemo(() => {
//     const startIndex = (page - 1) * limit;
//     const endIndex = startIndex + limit;
//     return filteredProducts.slice(startIndex, endIndex);
//   }, [filteredProducts, page, limit]);

//   // Products to display
//   const displayProducts = paginatedProducts;

//   // Calculate total pages based on filtered results
//   const totalPages = useMemo(() => {
//     return Math.ceil(filteredProducts.length / limit) || 1;
//   }, [filteredProducts.length, limit]);

//   // Total products count
//   const totalProductsCount = filteredProducts.length;

//   // Reset to page 1 when filter or search changes
//   useEffect(() => {
//     setPage(1);
//   }, [filter, searchQuery]);

//   // Handle page change
//   const handlePageChange = (event, value) => {
//     setPage(value);
//   };

//   // Handle limit change
//   const handleLimitChange = (event) => {
//     const newLimit = parseInt(event.target.value);
//     setLimit(newLimit);
//     setPage(1);
//   };

//   // Handle search
//   const handleSearch = () => {
//     setPage(1);
//   };

//   // Clear search
//   const handleClearSearch = () => {
//     setSearchQuery("");
//     setPage(1);
//   };

//   // Handle Enter key for search
//   const handleKeyPress = (e) => {
//     if (e.key === 'Enter') {
//       handleSearch();
//     }
//   };

//   // Quantity Change
//   const handleQtyChange = (id, value) => {
//     if (value < 0) return;
//     setQuantities({ ...quantities, [id]: value });
//   };

//   // Increase quantity
//   const handleIncreaseQty = (id) => {
//     const currentQty = quantities[id] || 0;
//     handleQtyChange(id, currentQty + 1);
//   };

//   // Decrease quantity
//   const handleDecreaseQty = (id) => {
//     const currentQty = quantities[id] || 0;
//     if (currentQty > 0) {
//       handleQtyChange(id, currentQty - 1);
//     }
//   };

//   // ADD All to Cart
//   const handleAddAll = async () => {
//     const items = Object.entries(quantities)
//       .filter(([id, qty]) => {
//         const productExists = displayProducts?.some(p => p._id === id);
//         return qty > 0 && productExists;
//       })
//       .map(([id, qty]) => ({
//         productId: id,
//         quantity: qty,
//       }));

//     if (items?.length === 0)
//       return toast.info("Please enter quantity for at least one product.");

//     try {
//       await dispatch(addToCart({ userId, items })).unwrap();
//       toast.success("Products added to cart!");
//       const addedProductCount = items.length;
//       setLocalCartCount((prev) => prev + addedProductCount);
//       const sellerIds = [
//         ...new Set(allProducts?.map((p) => p.user?._id).filter(Boolean)),
//       ];
//       await Promise.all(
//         sellerIds?.map((sellerId) =>
//           dispatch(getCart({ userId, seller: sellerId })).unwrap()
//         )
//       );

//       setQuantities({});
//     } catch (error) {
//       toast.error("Failed to add items to cart");
//     }
//   };

//   // Extract seller & buyer categories
//   const sellerIds = useMemo(
//     () => [...new Set(allProducts?.map((p) => p.user?._id).filter(Boolean))],
//     [allProducts]
//   );

//   const buyerCategories = useMemo(
//     () => [
//       ...new Set(
//         allProducts
//           ?.flatMap((p) => p.productVisibility?.map((v) => v.buyerCategory))
//           .filter(Boolean)
//       ),
//     ],
//     [allProducts]
//   );

//   useEffect(() => {
//     if (userId && sellerIds.length > 0) {
//       dispatch(getCart({ userId, seller: sellerIds, buyerCategories }));
//     }
//   }, [dispatch, userId, sellerIds, buyerCategories]);

//   // PRODUCT CATEGORIES FOR FILTER DROPDOWN
//   const productCategories = useMemo(() => {
//     return [
//       { key: "All", label: "All Products" },
//       ...allCategories.map((cat) => ({ key: cat, label: cat })),
//     ];
//   }, [allCategories]);

//   // Total Cart Count
//   const totalCartCount = useMemo(() => {
//     const backendCount = Array.isArray(cart) ? cart.length : 0;
//     return backendCount + localCartCount;
//   }, [cart, localCartCount]);

//   return (
//     <Box
//       sx={{
//         px: { xs: 2, md: 4 },
//         py: 3,
//         backgroundColor: "#f5f7fa",
//         minHeight: "100vh",
//       }}
//     >
//       {/* Header */}
//       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
//         <Typography fontWeight={700} fontSize={26}>
//           View Products
//         </Typography>
        
//         <Badge
//           badgeContent={totalCartCount}
//           color="primary"
//           anchorOrigin={{ vertical: "top", horizontal: "right" }}
//         >
//           <IconButton
//             onClick={() => {
//               navigate("/buyer-dashboard/cart", {
//                 state: { buyerCategories, sellerIds, userId },
//               });
//             }}
//             sx={{
//               backgroundColor: "#e0f2f1",
//               borderRadius: "12px",
//               width: 56,
//               height: 56,
//               color: "#22364a",
//               "&:hover": { backgroundColor: "#b2dfdb" },
//             }}
//           >
//             <ShoppingCartIcon fontSize="medium" />
//           </IconButton>
//         </Badge>
//       </Box>

//       {/* Filters Section */}
//       <Box
//         sx={{
//           display: "flex",
//           flexDirection: { xs: "column", md: "row" },
//           gap: 2,
//           mb: 3,
//           alignItems: { xs: "stretch", md: "center" },
//         }}
//       >
//         {/* Search Field */}
//         <Box sx={{ flex: 1 }}>
//           <TextField
//             fullWidth
//             placeholder="Search products by name, description, or category..."
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//             onKeyPress={handleKeyPress}
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <SearchIcon />
//                 </InputAdornment>
//               ),
//               endAdornment: (
//                 <InputAdornment position="end">
//                   {searchQuery && (
//                     <Button
//                       size="small"
//                       onClick={handleClearSearch}
//                       sx={{ mr: 0.5, fontSize: '0.75rem' }}
//                     >
//                       Clear
//                     </Button>
//                   )}
//                 </InputAdornment>
//               ),
//             }}
//             sx={{
//               backgroundColor: "white",
//               borderRadius: 2,
//               "& .MuiOutlinedInput-root": {
//                 borderRadius: 2,
//               },
//             }}
//             size="small"
//           />
//         </Box>

//         {/* Category Dropdown */}
//         <Box sx={{ minWidth: 200 }}>
//           <FormControl fullWidth size="small">
//             <InputLabel id="category-select-label">Category</InputLabel>
//             <Select
//               labelId="category-select-label"
//               id="category-select"
//               value={filter}
//               label="Category"
//               onChange={(e) => setFilter(e.target.value)}
//               sx={{
//                 backgroundColor: "white",
//                 borderRadius: 2,
//               }}
//               MenuProps={{
//                 PaperProps: {
//                   sx: {
//                     maxHeight: 300,
//                   },
//                 },
//               }}
//               IconComponent={ExpandMoreIcon}
//             >
//               {productCategories.map((category) => (
//                 <MenuItem key={category.key} value={category.key}>
//                   {category.label}
//                 </MenuItem>
//               ))}
//             </Select>
//           </FormControl>
//         </Box>

//         {/* Items per page selector */}
//         <Box sx={{ minWidth: 140 }}>
//           <FormControl fullWidth size="small">
//             <InputLabel id="limit-select-label">Items per page</InputLabel>
//             <Select
//               labelId="limit-select-label"
//               value={limit.toString()}
//               label="Items per page"
//               onChange={handleLimitChange}
//               sx={{
//                 backgroundColor: "white",
//                 borderRadius: 2,
//               }}
//             >
//               <MenuItem value="12">12 per page</MenuItem>
//               <MenuItem value="24">24 per page</MenuItem>
//               <MenuItem value="36">36 per page</MenuItem>
//               <MenuItem value="48">48 per page</MenuItem>
//             </Select>
//           </FormControl>
//         </Box>
//       </Box>

//       {/* Active Filters Display */}
//       {(filter !== "All" || searchQuery.trim()) && (
//         <Box
//           sx={{
//             display: "flex",
//             alignItems: "center",
//             gap: 1,
//             flexWrap: "wrap",
//             mb: 2,
//           }}
//         >
//           <Typography variant="body2" color="textSecondary">
//             Active filters:
//           </Typography>
//           {filter !== "All" && (
//             <Chip
//               label={`Category: ${filter}`}
//               size="small"
//               onDelete={() => setFilter("All")}
//               sx={{ fontSize: "0.75rem" }}
//             />
//           )}
//           {searchQuery.trim() && (
//             <Chip
//               label={`Search: "${searchQuery}" (${filteredProducts.length} found)`}
//               size="small"
//               onDelete={handleClearSearch}
//               sx={{ fontSize: "0.75rem" }}
//             />
//           )}
//         </Box>
//       )}

//       {/* Results Summary */}
//       {!loading && (
//         <Box sx={{ mb: 2 }}>
//           <Typography variant="body1" color="textSecondary">
//             Showing {(page - 1) * limit + 1} to{" "}
//             {Math.min(page * limit, totalProductsCount)} of{" "}
//             {totalProductsCount} products
//           </Typography>
//         </Box>
//       )}

//       {/* Loading State */}
//       {loading && displayProducts.length === 0 && (
//         <Box sx={{ textAlign: 'center', py: 8 }}>
//           <Typography variant="h6" color="textSecondary">
//             Loading products...
//           </Typography>
//         </Box>
//       )}

//       {/* Error State */}
//       {error && !loading && (
//         <Box sx={{ textAlign: 'center', py: 8, color: 'error.main' }}>
//           <Typography variant="h6">Error: {error}</Typography>
//         </Box>
//       )}

//       {/* Products Grid */}
//       {!loading && (
//         <Grid container spacing={3} sx={{ mb: 4 }}>
//           {displayProducts.length > 0 ? (
//             displayProducts.map((product, index) => {
//               const serialNumber = (page - 1) * limit + index + 1;
//               const productQty = quantities[product._id] || 0;
              
//               return (
//                 <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
//                   <Card
//                     sx={{
//                       height: '100%',
//                       display: 'flex',
//                       flexDirection: 'column',
//                       borderRadius: 2,
//                       boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
//                       transition: 'transform 0.2s, box-shadow 0.2s',
//                       '&:hover': {
//                         transform: 'translateY(-4px)',
//                         boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
//                       },
//                     }}
//                   >
//                     <CardContent sx={{ flexGrow: 1, p: 2 }}>
//                       {/* Product Serial Number */}
//                       <Typography
//                         variant="caption"
//                         color="textSecondary"
//                         sx={{
//                           display: 'inline-block',
//                           mb: 1,
//                           backgroundColor: '#f0f0f0',
//                           px: 1,
//                           py: 0.25,
//                           borderRadius: 1,
//                           fontWeight: 600,
//                         }}
//                       >
//                         #{serialNumber}
//                       </Typography>

//                       {/* Product Name */}
//                       <Typography
//                         variant="h6"
//                         sx={{
//                           fontWeight: 700,
//                           fontSize: '16px',
//                           color: '#22364a',
//                           mb: 1,
//                           cursor: 'pointer',
//                           '&:hover': {
//                             color: '#fb8900',
//                           },
//                         }}
//                         onClick={() =>
//                           navigate(
//                             `/buyer-dashboard/product-details/${product._id}`,
//                             { state: { product } }
//                           )
//                         }
//                       >
//                         {product.name}
//                       </Typography>

//                       {/* Category */}
//                       <Typography
//                         variant="body2"
//                         color="textSecondary"
//                         sx={{
//                           mb: 1,
//                           fontSize: '13px',
//                           fontWeight: 500,
//                         }}
//                       >
//                         {product.category?.name || "Uncategorized"}
//                       </Typography>

//                       {/* Price */}
//                       <Typography
//                         variant="h5"
//                         sx={{
//                           color: '#fb8900',
//                           fontWeight: 800,
//                           mb: 2,
//                           fontSize: '20px',
//                         }}
//                       >
//                         ₹{product.price}
//                       </Typography>

//                       {/* Description (truncated) */}
//                       {product.description && (
//                         <Typography
//                           variant="body2"
//                           color="textSecondary"
//                           sx={{
//                             fontSize: '13px',
//                             mb: 2,
//                             lineHeight: 1.4,
//                             display: '-webkit-box',
//                             WebkitLineClamp: 2,
//                             WebkitBoxOrient: 'vertical',
//                             overflow: 'hidden',
//                           }}
//                         >
//                           {product.description}
//                         </Typography>
//                       )}
//                     </CardContent>

//                     <Divider />

//                     <CardActions sx={{ p: 2, pt: 1.5 }}>
//                       {/* Quantity Controls */}
//                       <Box
//                         sx={{
//                           display: 'flex',
//                           alignItems: 'center',
//                           justifyContent: 'space-between',
//                           width: '100%',
//                         }}
//                       >
//                         <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                           <IconButton
//                             size="small"
//                             onClick={() => handleDecreaseQty(product._id)}
//                             sx={{
//                               backgroundColor: '#f5f5f5',
//                               borderRadius: '8px 0 0 8px',
//                               border: '1px solid #e0e0e0',
//                               borderRight: 'none',
//                               width: 32,
//                               height: 32,
//                             }}
//                             disabled={productQty === 0}
//                           >
//                             <RemoveIcon fontSize="small" />
//                           </IconButton>
                          
//                           <TextField
//                             type="number"
//                             value={productQty}
//                             onChange={(e) =>
//                               handleQtyChange(
//                                 product._id,
//                                 parseInt(e.target.value) || 0
//                               )
//                             }
//                             sx={{
//                               width: 60,
//                               '& .MuiInputBase-root': {
//                                 height: 32,
//                                 borderRadius: 0,
//                                 border: '1px solid #e0e0e0',
//                                 borderLeft: 'none',
//                                 borderRight: 'none',
//                               },
//                               '& .MuiOutlinedInput-input': {
//                                 textAlign: 'center',
//                                 padding: '6px 8px',
//                               },
//                               '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
//                                 display: 'none',
//                               },
//                               '& input[type=number]': {
//                                 MozAppearance: 'textfield',
//                               },
//                             }}
//                             inputProps={{
//                               min: 0,
//                               style: { fontSize: '14px' }
//                             }}
//                           />
                          
//                           <IconButton
//                             size="small"
//                             onClick={() => handleIncreaseQty(product._id)}
//                             sx={{
//                               backgroundColor: '#f5f5f5',
//                               borderRadius: '0 8px 8px 0',
//                               border: '1px solid #e0e0e0',
//                               borderLeft: 'none',
//                               width: 32,
//                               height: 32,
//                             }}
//                           >
//                             <AddIcon fontSize="small" />
//                           </IconButton>
//                         </Box>

//                         {/* Add to Cart Button for individual product */}
//                         <Button
//                           variant="contained"
//                           size="small"
//                           onClick={() => {
//                             if (productQty > 0) {
//                               handleAddAll(); // This will add all products with quantity > 0
//                             }
//                           }}
//                           disabled={productQty === 0}
//                           sx={{
//                             borderRadius: '20px',
//                             fontWeight: 600,
//                             backgroundColor: productQty > 0 ? '#fb8900' : '#e0e0e0',
//                             color: productQty > 0 ? 'white' : '#999',
//                             px: 2,
//                             py: 0.75,
//                             fontSize: '13px',
//                             minWidth: 100,
//                             '&:hover': {
//                               backgroundColor: productQty > 0 ? '#e67a00' : '#e0e0e0',
//                             },
//                           }}
//                         >
//                           Add to Cart
//                         </Button>
//                       </Box>
//                     </CardActions>
//                   </Card>
//                 </Grid>
//               );
//             })
//           ) : (
//             <Grid item xs={12}>
//               <Paper
//                 sx={{
//                   textAlign: 'center',
//                   py: 8,
//                   borderRadius: 2,
//                   backgroundColor: 'white',
//                 }}
//               >
//                 <Typography variant="h6" color="textSecondary">
//                   {searchQuery.trim() || filter !== "All"
//                     ? `No products found${filter !== "All" ? ` in category "${filter}"` : ''}${searchQuery.trim() ? ` matching "${searchQuery}"` : ''}`
//                     : "No products available"}
//                 </Typography>
//               </Paper>
//             </Grid>
//           )}
//         </Grid>
//       )}

//       {/* Pagination */}
//       {totalPages > 1 && !loading && (
//         <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
//           <Stack spacing={2}>
//             <Pagination
//               count={totalPages}
//               page={page}
//               onChange={handlePageChange}
//               color="primary"
//               size="large"
//               showFirstButton
//               showLastButton
//               siblingCount={1}
//               boundaryCount={1}
//               sx={{
//                 '& .MuiPaginationItem-root': {
//                   fontWeight: 600,
//                 },
//                 '& .Mui-selected': {
//                   backgroundColor: '#fb8900 !important',
//                   color: 'white',
//                 }
//               }}
//             />
//           </Stack>
//         </Box>
//       )}

//       {/* Add All to Cart Button (Sticky at bottom) */}
//       {displayProducts.length > 0 && (
//         <Box
//           sx={{
//             position: 'sticky',
//             bottom: 20,
//             zIndex: 100,
//             display: 'flex',
//             justifyContent: 'center',
//             mt: 2,
//           }}
//         >
//           <Button
//             variant="contained"
//             onClick={handleAddAll}
//             disabled={Object.values(quantities).filter(qty => qty > 0).length === 0}
//             startIcon={<ShoppingCartIcon />}
//             sx={{
//               px: 6,
//               py: 1.5,
//               borderRadius: '50px',
//               fontWeight: 700,
//               fontSize: '16px',
//               backgroundColor: "#fb8900",
//               color: "#fff",
//               boxShadow: '0 4px 12px rgba(251, 137, 0, 0.3)',
//               "&:hover": {
//                 backgroundColor: "#e67a00",
//                 boxShadow: '0 6px 16px rgba(251, 137, 0, 0.4)',
//               },
//               "&.Mui-disabled": {
//                 backgroundColor: "#f0f0f0",
//                 color: "#aaa",
//                 boxShadow: 'none',
//               },
//             }}
//           >
//             Add All Selected to Cart
//           </Button>
//         </Box>
//       )}
//     </Box>
//   );
// }